import { type ReportTemplate } from 'reportingSolution/constants'

const config: ReportTemplate = {
  id: 'shopping_insights_v3',
  active: true,
  label: 'Shopping Insights Report V3',
  category: 'Product analysis',
  description:
    'Comprehensive shopping performance analysis including merchant center insights, price benchmarks, and competitive visibility',
  image_url: '-',
  authors: [
    {
      name: 'Tobias Hink',
      email: 'hink@precisdigital.com',
      image_url: 'https://lh3.googleusercontent.com/a-/AOh14GjyNEc4WJk3HgY11avoS-2opMitZKNbLBZcaWvS=s126-p-k-rw-no'
    }
  ],
  documentation: {
    type: '-',
    link: '-',
    link_description: '-'
  },
  dashboard: {
    template_report_id: '93206270-0839-47c8-bcb8-cebc8f54fa84',
    report_name_template: 'copy of Shopping Insights V3 - {today}',
    datasources: [
      {
        alias: 'performance',
        datasourceName: 'Performance - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_performance_view_{name}',
        isPartitioned: 'false',
        refreshFields: 'false'
      },
      {
        alias: 'competitors',
        datasourceName: 'Competitors - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_competitors_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'issues',
        datasourceName: 'Issues - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_issues_{name}',
        isPartitioned: 'false',
        refreshFields: 'false'
      },
      {
        alias: 'cross_sell',
        datasourceName: 'Cross Sell - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_cross_sell_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'price_benchmarks',
        datasourceName: 'Price Benchmarks - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_price_benchmarks_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'price_suggestions',
        datasourceName: 'Price Suggestions - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_price_suggestions_{name}',
        isPartitioned: 'false',
        refreshFields: 'false'
      },
      {
        alias: 'products_historical',
        datasourceName: 'Products Historical - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_products_historical_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'organic',
        datasourceName: 'Organic - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_organic_performance_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      },
      {
        alias: 'feed_checks',
        datasourceName: 'Feed Checks - {name}',
        type: 'TABLE',
        tableId: 'shopping_insights_feed_checks_{name}',
        isPartitioned: 'false',
        refreshFields: 'false'
      }
    ]
  },
  sources: {
    merchant: {
      reports: [
        'products',
        'products_historical',
        'price_competitiveness_historical',
        'competitive_visibility_competitor_view',
        'competitive_visibility_benchmark_view',
        'price_insights_product_view',
        'product_view_potential',
        'organic_performance'
      ]
    },
    gads: {
      reports: [
        'shopping_insights_base',
        'shopping_insights_categories',
        'shopping_insights_base_totals',
        'shopping_insights_cross_sell',
        'shopping_insights_impr_share',
        'shopping_insights_title_meta_data',
        'shopping_insights_types_meta_data',
        'customer_campaign_meta_data'
      ]
    }
  },
  inputs: {
    other: [
      {
        id: 'targetCurrency',
        type: 'dropdown',
        label: 'Target Currency',
        defaultValue: 'EUR',
        tooltip: 'The currency to transform all currency-based columns (cost, revenue, etc) to.',
        required: true,
        mutable: true
      }
    ],
    merchant: [
      {
        id: 'createMerchantOutputs',
        type: 'boolean',
        label: 'Create Merchant Outputs for Alvie Shopping Labeller',
        defaultValue: false,
        tooltip: 'Enable to generate merchant-specific output tables for supplemental feeds (Alvie Shopping Labeller)',
        helperText:
          'Enable to generate merchant-specific output tables for supplemental feeds (Alvie Shopping Labeller)',
        required: false,
        mutable: true
      }
    ],
    gads: [
      {
        id: 'gadsCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'customer__descriptive_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'customer__id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign__name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign__id',
            type: 'string'
          },
          {
            label: 'Campaign status',
            value: 'campaign__status',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'campaign__advertising_channel_type',
            type: 'string'
          },
          {
            label: 'Campaign sub type',
            value: 'campaign__advertising_channel_sub_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign__name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'gadsMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'customer__descriptive_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'customer__id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign__name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign__id',
            type: 'string'
          },
          {
            label: 'Campaign status',
            value: 'campaign__status',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'campaign__advertising_channel_type',
            type: 'string'
          },
          {
            label: 'Campaign sub type',
            value: 'campaign__advertising_channel_sub_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'global'
                }
              ]
            }
          },
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-de-'
                }
              ]
            }
          }
        ]
      }
    ]
  }
}

export default config
